import React from "react";
import cropr, { RecordModel } from "../../shared/utils/cropr";
import gettext from "../../shared/utils/gettext";
import { formatNumber } from "./utils";
// @ts-ignore
import { RIEInput } from "riek";
import moment from "moment";
import _ from "underscore";

interface InlineProp {
  property?: string;
  name?: string;
  detailType?: number;
  unit?: string;
  factor?: number;
}

// cropr.data is pas op runtime beschikbaar, daarom initialisatie uitstellen, maar wel cachen
const getThemeInline = _.memoize(() => {
  const map: Record<string, InlineProp[]> = {};
  map[cropr.data.THEME.PLANTING_SEEDING] = [{ detailType: 1000602 }];
  map[cropr.data.THEME.IRRIGATION] = [{ detailType: 1000900, factor: 10, unit: "m³" }];
  map[cropr.data.THEME.HARVESTING] = [{ detailType: 1001000 }]; // 1001004 ?
  return map;
});

const getPropertyInline: () => Record<string, InlineProp[]> = _.memoize(() => ({
  work_cost: [{ property: "work_cost", name: gettext("Work cost"), unit: "€" }],
  used_fuel: [{ property: "used_fuel", name: gettext("Used Fuel"), unit: "l" }],
}));

const changeValue = (p: Record<string, string>) => {
  const [key, keyval] = Object.entries(p)[0];
  let value = parseFloat(String(keyval).replace(/[€ ]/g, "").replace(",", ".") || "0");
  if (key.startsWith("record:")) {
    const args = key.split("__");
    const record = cropr.records.get(parseInt(args[0].split(":")[1]));
    if (!record) return console.error("Can not find record for " + key);
    const where: Record<string, any> = {};
    for (let i = 1; i < args.length; i++) {
      let [k, v] = args[i].split(":");
      if (k === "total") {
        // add "total" in key, and we divide the value by area
        value /= record.getArea() * parseInt(v || "1");
        continue;
      } else if (k === "set") {
        // 'set' is a shortcut for setting a value
        const attrs: any = {};
        attrs[v] = v === "duration" ? moment(keyval, "HH:mm").format("HH:mm") : value;
        return record.save(attrs);
      }
      where[k] = /\d+/.test(v) ? parseInt(v) : v;
    }
    const activities = $.extend(true, {}, { activities: record.get("activities") });
    const item = where.detailtype
      ? record.getItemFromActivity(parseInt(where.detailtype), activities.activities)
      : record.findActivityItemWhere(where, activities.activities);
    if (!item) return console.error("Can not find recordItem", key, record);
    item.value = value;
    record.save(activities);
    return;
  }
};
const format = (value?: number) => formatNumber(value, false, false, 3);
interface RecordInline {
  record: RecordModel;
  dosage?: number;
}

interface InlineRecordTableProps {
  filter: any;
}
const InlineRecordTable = ({ filter }: InlineRecordTableProps) => {
  const { cropfields = [], stock, method, applier, theme, attributes } = filter;
  const records = cropr.records.reduce<RecordInline[]>((memo, record: RecordModel) => {
    if (
      record.get("processing_state") === "performed" &&
      cropfields.includes(record.get("cropfield"))
    ) {
      const dosage = stock && record.findActivityItemWhere({ stock })?.value;
      if (stock && !dosage) return memo;
      if (method && !record.get("activities").find((a: any) => a.type === method)) return memo;
      if (theme && !record.get("activities").find((a: any) => a.theme === theme)) return memo;
      for (const [key, value] of Object.entries(attributes || {})) {
        const v = "boolean" === typeof value ? Boolean(record.get(key)) : record.get(key);
        if (v !== value) return memo;
      }
      if (
        applier &&
        !(record.get("applier") === applier || record.get("coworkers").includes(applier))
      )
        return memo;
      memo.push({
        record,
        dosage,
      });
    }
    return memo;
  }, []);
  if (records.length === 0) console.error("No records", cropfields, filter);
  const inlines: InlineProp[] = [];
  if (theme) inlines.push(...(getThemeInline()[theme] || []));
  Object.entries(attributes || {}).forEach(([name, value]) => {
    const v = getPropertyInline()[name];
    if (v && value) inlines.push(...v);
  });
  const show = { applier: method || theme, duration: method || (applier && !inlines?.length) };
  return (
    <table className="inline-record-edit-overlay table table-striped">
      <thead>
        {inlines && (
          <tr>
            <th colSpan={show.applier || applier ? 4 : 3}></th>
            {inlines?.map(({ detailType, name }, i) => (
              <th colSpan={2} key={i}>
                {detailType ? cropr.detailtype[detailType]?.name : name}
              </th>
            ))}
          </tr>
        )}
        <tr>
          <th>{gettext("Date")}</th>
          <th>{gettext("Cropfield")}</th>
          <th>{cropr.l10n.unit_area_display}</th>
          {stock && (
            <th>
              {cropr.stock.get(stock)?.getUnit()}/{cropr.l10n.unit_area_display}
            </th>
          )}
          {stock && <th>{gettext("Total")}</th>}
          {show.applier && <th>{gettext("App.")}</th>}
          {applier && <th>{gettext("Method")}</th>}
          {show.duration && <th>{gettext("Duration")}</th>}
          {inlines?.map((_, i) => (
            <React.Fragment key={i}>
              <th>/{cropr.l10n.unit_area_display}</th>
              <th>{gettext("Total")}</th>
            </React.Fragment>
          ))}
          <td />
        </tr>
      </thead>
      <tbody>
        {records.map(({ record, dosage }) => (
          <tr key={record.id}>
            <td>{record.getLocaleDate("date")}</td>
            <td>
              <i>{record.getRelated("cropfield", "name")}</i>
            </td>
            <td className="text-right">{record.getArea().toFixed(2)}</td>
            {stock && (
              <td className="text-right">
                <RIEInput
                  value={format(dosage)}
                  propName={`record:${record.id}__stock:${stock}`}
                  change={changeValue}
                  className="editable"
                />
              </td>
            )}
            {stock && (
              <td className="text-right">
                <RIEInput
                  value={format(record.getArea() * (dosage || 0))}
                  propName={`record:${record.id}__stock:${stock}__total`}
                  change={changeValue}
                  className="editable"
                />
              </td>
            )}
            {show.applier && <td>{record.getShortApplierName()}</td>}
            {applier && (
              <td>
                <i>{record.getOperationList()}</i>
              </td>
            )}
            {show.duration && (
              <td className="text-right">
                <RIEInput
                  value={record.get("duration")}
                  propName={`record:${record.id}__set:duration`}
                  change={changeValue}
                  className="editable"
                />
              </td>
            )}
            {inlines?.map(({ detailType, property, unit, factor }) => {
              const value = detailType
                ? record.getValueFromActivity(detailType)
                : property && record.get(property);
              const isCurrency = unit === "€";
              const prop = detailType ? `detailtype:${detailType}` : `set:${property}`;
              return (
                <React.Fragment key={prop}>
                  <td className="text-right">
                    {value && (
                      <RIEInput
                        value={formatNumber(value, isCurrency, false, isCurrency ? 2 : 3)}
                        propName={`record:${record.id}__${prop}`}
                        change={changeValue}
                        className="editable"
                      />
                    )}{" "}
                    {detailType && cropr.detailtype[detailType]?.unit}
                  </td>
                  <td className="text-right">
                    {value && (
                      <RIEInput
                        value={formatNumber(
                          record.getArea() * (factor || 1) * (value || 0),
                          isCurrency
                        )}
                        propName={`record:${record.id}__total:${factor || ""}__${prop}`}
                        change={changeValue}
                        className="editable"
                      />
                    )}{" "}
                    {(!isCurrency && unit) ||
                      (detailType && cropr.detailtype[detailType]?.unit?.replace("/ha", ""))}
                  </td>
                </React.Fragment>
              );
            })}
            <td>
              <a href={`#edit/${record.id}`}>
                <i className="far fa-edit" />
              </a>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
export default InlineRecordTable;
